import { SIMAElementClassifier, } from "../types";
/**
 * 横断構成点 (I02 レコード)。
 * 追加距離に対する中心線からの離れ (offsetDistance: 負=左, 正=右) と地盤高を保持します。
 */
export class TransversePoint {
    classifier = SIMAElementClassifier.CROSS_SECTION_POINT;
    pointName;
    offsetDistance;
    groundElevation;
    constructor(data) {
        this.pointName = data?.pointName ?? data?.point_name ?? "";
        this.offsetDistance = data?.offsetDistance ?? data?.offset_distance ?? 0;
        this.groundElevation = data?.groundElevation ?? data?.ground_elevation ?? 0;
    }
    get point_name() {
        return this.pointName;
    }
    set point_name(val) {
        this.pointName = val;
    }
    get offset_distance() {
        return this.offsetDistance;
    }
    set offset_distance(val) {
        this.offsetDistance = val;
    }
    get isLeft() {
        return this.offsetDistance < 0;
    }
    get isRight() {
        return this.offsetDistance > 0;
    }
    get isCenter() {
        return Math.abs(this.offsetDistance) < 1e-6;
    }
    toPointData() {
        return {
            classifier: SIMAElementClassifier.CROSS_SECTION_POINT,
            point_name: this.pointName,
            offset_distance: this.offsetDistance,
            ground_elevation: this.groundElevation,
        };
    }
}
/**
 * 単一横断測点 (I01 中心 + I02 構成点一覧)。
 * 冗長な center 構造体をフラット化し、直接フィールドとして保持します。
 */
export class CrossSectionStation {
    sectionName;
    chainage;
    groundElevation;
    stakeElevation;
    transversePoints = [];
    constructor(data) {
        const input = data;
        const center = input?.center;
        this.sectionName = input?.sectionName ?? center?.section_name ?? "";
        this.chainage = input?.chainage ?? center?.chainage ?? 0;
        this.groundElevation =
            input?.groundElevation ?? center?.ground_elevation ?? 0;
        this.stakeElevation = input?.stakeElevation ?? center?.stake_elevation ?? 0;
        if (data?.transversePoints) {
            this.transversePoints = data.transversePoints.map((p) => p instanceof TransversePoint ? p : new TransversePoint(p));
        }
    }
    /**
     * 左側の横断構成点一覧を取得します。
     */
    get leftPoints() {
        return this.transversePoints
            .filter((p) => p.isLeft)
            .sort((a, b) => a.offsetDistance - b.offsetDistance);
    }
    /**
     * 右側の横断構成点一覧を取得します。
     */
    get rightPoints() {
        return this.transversePoints
            .filter((p) => p.isRight)
            .sort((a, b) => a.offsetDistance - b.offsetDistance);
    }
    /**
     * 中心点 (離れ 0) を取得します。
     */
    get centerPoint() {
        return this.transversePoints.find((p) => p.isCenter);
    }
    /**
     * 横断構成点を追加し、離れの昇順でソートします。
     */
    addTransversePoint(point) {
        const instance = point instanceof TransversePoint ? point : new TransversePoint(point);
        this.transversePoints.push(instance);
        this.transversePoints.sort((a, b) => a.offsetDistance - b.offsetDistance);
        return instance;
    }
    /**
     * ワイヤフォーマット (ISIMACrossSectionStation 構造) に変換します。
     */
    toStationData() {
        return {
            center: {
                classifier: SIMAElementClassifier.CROSS_SECTION_CENTER,
                section_name: this.sectionName,
                chainage: this.chainage,
                ground_elevation: this.groundElevation,
                stake_elevation: this.stakeElevation,
            },
            transversePoints: this.transversePoints.map((p) => p.toPointData()),
        };
    }
}
/**
 * 路線横断測量成果グループ (I00..I99 ブロック)。
 * 路線番号 (routeNumber) と路線名 (routeName) を持ちます。
 */
export class CrossSectionGroup {
    routeNumber;
    routeName;
    stations = [];
    constructor(data) {
        const input = data;
        this.routeNumber = input?.routeNumber ?? input?.alignment_number ?? 1;
        this.routeName = input?.routeName ?? input?.alignment_name ?? "";
        if (data?.stations) {
            this.stations = data.stations.map((s) => s instanceof CrossSectionStation ? s : new CrossSectionStation(s));
            this.sortStations();
        }
    }
    /**
     * 互換用プロパティ (routeNumber に委譲)
     */
    get alignmentNumber() {
        return this.routeNumber;
    }
    set alignmentNumber(val) {
        this.routeNumber = val;
    }
    /**
     * 互換用プロパティ (routeName に委譲)
     */
    get alignmentName() {
        return this.routeName;
    }
    set alignmentName(val) {
        this.routeName = val;
    }
    /**
     * 追加距離 (chainage) の昇順に横断測点をソートします。
     */
    sortStations() {
        this.stations.sort((a, b) => a.chainage - b.chainage);
    }
    /**
     * 横断測点を追加し、昇順ソートを維持します。
     */
    addStation(station) {
        const instance = station instanceof CrossSectionStation
            ? station
            : new CrossSectionStation(station);
        this.stations.push(instance);
        this.sortStations();
        return instance;
    }
    /**
     * 測面名または追加距離から横断測点を検索します。
     */
    findStation(chainageOrName) {
        if (typeof chainageOrName === "number") {
            return this.stations.find((s) => Math.abs(s.chainage - chainageOrName) < 1e-5);
        }
        return this.stations.find((s) => s.sectionName === chainageOrName);
    }
    /**
     * ワイヤフォーマット (ISIMACrossSectionGroup 構造) に変換します。
     */
    toGroupData() {
        return {
            alignment_number: this.routeNumber,
            alignment_name: this.routeName,
            stations: this.stations.map((s) => s.toStationData()),
        };
    }
}
//# sourceMappingURL=cross-section.js.map